import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import { intersectRect } from '../../../util/svg';
import dateUtils from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { escapeHTML } from '../../../util/text';

let highchartUtils = require('../../../util/highcharts')(Highcharts);

function drawLine(chart, propName, label, serie) {
  if (!serie.data.length) {
    return true;
  }

  let index =
    _.findIndex(serie.data, s => {
      return s.y > 0;
    }) - 1;
  index = index < 0 ? 0 : index;
  const firstPointWithValue = serie.data[index];
  const pointA_x = firstPointWithValue.plotX + chart.plotLeft;
  const pointA_y = firstPointWithValue.plotY + chart.plotTop;

  const lastPoint = _.last(serie.data);
  const pointB_x = lastPoint.plotX + chart.plotLeft;
  const pointB_y = lastPoint.plotY + chart.plotTop;

  if (!chart[propName]) {
    chart[propName] = {};
    chart[propName].line = chart.renderer
      .path(['M', pointA_x, pointA_y, 'L', pointB_x - pointA_x, pointB_y])
      .attr({
        zIndex: 3,
        stroke: '#000',
        'stroke-width': 2,
        'stroke-dasharray': '1,7',
        'stroke-linecap': 'round'
      })
      .add();
    chart[propName].label = chart.renderer
      .label(label, pointB_x, pointB_y, 'rect', null, null, false, null)
      .css({
        color: '#444444',
        border: `1px solid #ececec`
      })
      .attr({
        text: label,
        padding: 5,
        r: 2,
        zIndex: 3,
        color: '#444444',
        fill: '#fff',
        stroke: '#ececec',
        'stroke-width': 1
      })
      .add();
  }

  chart[propName].line
    .attr({
      d: 'M ' + pointA_x + ' ' + pointA_y + ' L ' + pointB_x + ' ' + pointB_y
    })
    .show();
  chart[propName].label
    .attr({
      text: label,
      fill: '#fff',
      x: pointB_x - chart[propName].label.width - 10,
      y: pointB_y - chart[propName].label.height - 10
    })
    .show();
}

function destroyLine(chart, propName) {
  if (chart[propName]) {
    chart[propName].line.hide();
    chart[propName].label.hide();
  }
}

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'area',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    split: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  xAxis: {
    type: 'datetime',
    tickmarkPlacement: 'on',
    title: {
      enabled: false
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    gridLineWidth: 1,
    gridLineColor: '#ececec',
    events: {}
  },
  yAxis: {
    opposite: false,
    allowDecimals: false,
    title: {
      text: 'Work items',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      zIndex: 2,
      style: {
        fontSize: '12px'
      }
    },
    gridLineWidth: 1,
    gridLineColor: '#ececec'
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    area: {
      stacking: 'normal',
      lineColor: '#ececec',
      lineWidth: 0,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false
          }
        }
      },
      events: {
        mouseOut: function() {
          if (this.chart.cross) {
            this.chart.cross.hide();
          }
          if (this.chart.pLbls) {
            _.forEach(this.chart.pLbls, lbl => {
              if (lbl) {
                lbl.hide();
                lbl.diamond.hide();
              }
            });
          }
        }
      }
    },
    series: {
      turboThreshold: 0,
      marker: {
        enabled: false
      },
      events: {}
    }
  },
  series: []
};

export class CFDChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  chartRendered = false;
  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartUtils.splitTooltipOverride();

    let that = this;
    chartConfig.tooltip.formatter = function(ev) {
      return that.tooltipFormatter(ev, this, that);
    };
    chartConfig.chart.events.redraw = function(ev) {
      return that.chartRendered && that.drawArrivalLine(ev);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.arrivalDepartureLines) {
      this.drawArrivalLine({ target: this.chart });
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.arrivalDepartureLines != this.props.arrivalDepartureLines || prevProps.stats != this.props.stats) {
      this.drawArrivalLine({ target: this.chart });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename
          }
        },
        false
      );
    }

    if (prevProps.excludeWeekends != this.props.excludeWeekends) {
      this.renderWeekends();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = []) {
    const diff = dataUtils.getDiff(oldData, this.props.data, 'data');
    highchartUtils.updateSeries(diff, this.chart);
    this.chart.redraw(false);
    this.chartRendered = true;

    setTimeout(() => {
      this.updateChartRange();
    }, 1000);
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('cfd', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateChartRange() {
    this.renderWeekends();
    this.chart.xAxis[0].setExtremes(+this.props.dates.startDate, +this.props.dates.endDate);
  }

  renderWeekends() {
    const bands = [];
    if (this.props.excludeWeekends) {
      const endDate = new Date(this.props.dates.endDate);
      const date = new Date(this.props.dates.startDate);
      while (date < endDate) {
        if (date.getDay() === 5 || date.getDay() === 6) {
          const day = new Date(date);
          bands.push({
            from: day.startOfDay(),
            to: day.endOfDay(),
            color: '#ececec'
          });
        }
        date.setDate(date.getDate() + 1);
      }
    }

    this.chart.update({
      xAxis: { plotBands: bands }
    });
  }

  tooltipFormatter(ev, chartScope, componentScope) {
    var s = [];
    s.push(moment(chartScope.x).format(componentScope.props.dateFormat));

    if (componentScope.props.wipStagesTooltip == 'stages') {
      let tooltipHtml = '';
      chartScope.points.forEach(point => {
        tooltipHtml += `<div className="tooltip_label_item" style="margin: 4px 5px 1px 0; line-height: 20px;"><span className="label_box" style="position: relative; top: 0px; display: inline-block; width: 8px; height: 8px; margin-left: 4px; margin-right: 8px; transform: rotate(45deg); background:${point.color}"></span>${escapeHTML(point.series.name)}: ${point.point.y} </div>`;
      });
      s.push(tooltipHtml);
    } else {
      s.push(
        `<span>Process: ${_(chartScope.points)
          .filter(p => !p.point.isDoneState)
          .sumBy('point.daily')}</span>`
      );
    }

    componentScope.renderCrosshair(ev, chartScope.points, componentScope.props.approxAvgStagesTooltip);
    return s;
  }

  //Approx average cycle time lines renderer
  renderCrosshair(ev, points, approxAvgStagesTooltip) {
    let chart = ev.chart;
    let height = 0;
    let lineOpacity = 0.7;
    chart.lblPos = [];

    if (!chart.cross) {
      chart.cross = chart.renderer
        .path()
        .attr({
          stroke: `rgba(0,0,0, ${lineOpacity})`,
          'stroke-width': 1,
          zIndex: 3
        })
        .add();
    }
    if (!chart.pLbls) {
      chart.pLbls = [];
    }

    let path = [];

    let tooltipPoints;

    if (approxAvgStagesTooltip != 'system') {
      tooltipPoints = points;
    } else {
      tooltipPoints = [points[0], points[points.length - 1]];
    }

    _.forEach(tooltipPoints, (point, index) => {
      let p = point.point;
      let x = p.plotX + chart.plotLeft;
      let y = p.plotY + chart.plotTop;
      let color,
        symbolSize = 10;

      if (index === 0) {
        height = chart.plotSizeY - p.plotY;
        path = ['M', x, y, 'v', 0, height];
      }

      if (index > 0) {
        let pIndex = _.indexOf(p.series.points, p);
        let crossPointX = 0,
          approxAvgCycleTime = 0;

        let bandFound = false,
          bandIndex = index - 1;
        while (!bandFound && bandIndex > 0) {
          let bandLeftPoints = points[bandIndex].point.series.points.slice(0, pIndex + 1);
          bandFound = _.sumBy(bandLeftPoints, 'daily') > 0;
          if (!bandFound) {
            bandIndex -= 1;
          }
        }

        color = points[index - 1].point.color;
        let leftPoints = points[bandIndex].point.series.points.slice(0, pIndex + 1);
        for (let lp = leftPoints.length - 1; lp >= 0; lp--) {
          let prevLp = lp > 0 ? lp - 1 : 0;
          let leftPoint = leftPoints[lp],
            prevLeftPoint = leftPoints[prevLp];
          if (leftPoint.plotY == p.plotY) {
            crossPointX = x - leftPoint.plotX - chart.plotLeft;
            approxAvgCycleTime = Math.round((p.x - leftPoints[lp].x) / 8.64e7);
          } else if (leftPoint.plotY <= p.plotY && prevLeftPoint.plotY >= p.plotY) {
            crossPointX = x - prevLeftPoint.plotX - chart.plotLeft;
            approxAvgCycleTime = Math.round((p.x - leftPoints[prevLp].x) / 8.64e7);
          }
        }

        path = path.concat(['M', x, y, 'h', -crossPointX, 0]);

        let approxAvgCycleTimeLbl = approxAvgCycleTime + ' day' + (approxAvgCycleTime != 1 ? 's' : '');

        if (!chart.pLbls[index]) {
          chart.pLbls[index] = chart.renderer
            .label(approxAvgCycleTime, x - crossPointX, y, 'rect', null, null, false, null)
            .css({
              color: '#444444',
              border: `1px solid ${color}`
            })
            .attr({
              padding: 5,
              r: 2,
              zIndex: 4,
              stroke: color,
              'stroke-width': 1
            })
            .add();

          chart.pLbls[index].diamond = chart.renderer
            .symbol('diamond', x - crossPointX, y, symbolSize, symbolSize)
            .attr({
              zIndex: 5,
              stroke: `rgba(0,0,0, ${lineOpacity})`,
              'stroke-width': 1
            })
            .add();
        }
        let lbl = chart.pLbls[index],
          diamond = lbl.diamond,
          prevLbl = chart.pLbls[index > 1 ? index - 1 : 1];

        let xLblPos = x - (crossPointX > 0 ? crossPointX : 10) - lbl.width - 20;
        let yLblPos = y - lbl.height / 2;

        if (xLblPos < 0) {
          xLblPos = 0;
        }

        if (index > 1 && intersectRect(prevLbl, lbl)) {
          yLblPos = prevLbl.y + prevLbl.height + 3;
        }

        if (approxAvgCycleTime > 0) {
          chart.lblPos.push({ x: xLblPos, y: yLblPos });

          lbl
            .attr({
              text: approxAvgCycleTimeLbl,
              fill: '#fff',
              x: xLblPos,
              y: yLblPos
            })
            .show();

          let xMarkerPos = x - (crossPointX > 0 ? crossPointX : 10) - symbolSize;
          if (xMarkerPos < 0) {
            xMarkerPos = 0;
          }

          diamond
            .attr({
              fill: color,
              x: xMarkerPos,
              y: y - symbolSize / 2
            })
            .show();
        } else {
          lbl.hide();
          diamond.hide();
        }
      }

      chart.cross.attr({ d: path }).show();
    });

    const tooltip = _(chart.lblPos)
      .filter(l => l.x > 0)
      .orderBy('y')
      .first();
    chart.leftLblPos = tooltip ? tooltip.x : 0;
  }

  drawArrivalLine(e) {
    const chart = e.target;

    if (!this.props.stats || !this.props.stats.length) {
      return true;
    }

    destroyLine(chart, 'arrival');
    destroyLine(chart, 'departure');

    if (!this.props.arrivalDepartureLines || !chart.series.length) {
      return true;
    }

    const arrivalSerie = _.first(chart.series);
    const arrivalRate = _.first(this.props.stats).avgArrivalRate;
    drawLine(chart, 'arrival', `${arrivalRate.toFixed(2)} items/day`, arrivalSerie);

    let doneData = _(chart.series).filter(
      s => this.props.dashboard.done.indexOf(s.userOptions.listId) > -1 || s.userOptions.listId === 'done'
    );
    if (doneData.value().length === 0) {
      return true;
    }

    doneData = doneData
      .map(d => d.data)
      .flatten()
      .filter(d => d.y > 0)
      .orderBy('x')
      .groupBy('x')
      .toPairs()
      .map(d => d[1])
      .value();

    let firstPoint = _.minBy(_.first(doneData), 'plotY');
    const lastPoint = _.minBy(_.last(doneData), 'plotY');

    if (!firstPoint) {
      return true;
    }

    if (firstPoint.index > 0) {
      firstPoint = firstPoint.series.data[firstPoint.index - 1];
    }

    const departureRate = _.last(this.props.stats).avgArrivalRate;
    drawLine(chart, 'departure', `${departureRate.toFixed(2)} items/day`, {
      data: [firstPoint, lastPoint]
    });
  }

  render() {
    return (
      <div id={this.props.id} className="chart_content">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full'}></div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    excludeWeekends: state.filters.excludeWeekends,
    wipStagesTooltip: state.filters.cfd.wipStagesTooltip,
    approxAvgStagesTooltip: state.filters.cfd.approxAvgStagesTooltip,
    arrivalDepartureLines: state.filters.cfd.arrivalDepartureLines,
    dashboard: state.dashboards.selected,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps)(CFDChart));
